import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  type Location,
  findLocationBySlug,
  nearestKota,
} from "@/data/locations";

const LS_KEY = "prayer:default";
const LS_PROMPT_KEY = "prayer:geo-prompted";

interface Stored {
  slug: string;
  /** present when user picked a kecamatan */
  kecSlug?: string;
}

interface Ctx {
  location: Location;
  /** True once we've resolved (SSR fallback = jakarta until hydrated). */
  ready: boolean;
  /** True if we should ask the user for geolocation consent. */
  shouldPrompt: boolean;
  setLocation: (loc: Location) => void;
  requestGeolocation: () => Promise<void>;
  dismissPrompt: () => void;
}

const LocationCtx = createContext<Ctx | null>(null);

const FALLBACK = (): Location => findLocationBySlug("dki-jakarta")!;

function readStored(): Location | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;
    const slug = parsed.kecSlug ?? parsed.slug;
    return findLocationBySlug(slug) ?? null;
  } catch {
    return null;
  }
}

function writeStored(loc: Location) {
  if (typeof window === "undefined") return;
  const payload: Stored =
    loc.kind === "kecamatan"
      ? { slug: loc.parentSlug ?? loc.slug, kecSlug: loc.slug }
      : { slug: loc.slug };
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(payload));
  } catch { /* ignore */ }
}

export function LocationProvider({ children }: { children: React.ReactNode }) {
  const [location, setLocationState] = useState<Location>(() => FALLBACK());
  const [ready, setReady] = useState(false);
  const [shouldPrompt, setShouldPrompt] = useState(false);

  // Hydrate from localStorage on mount; decide whether to ask.
  useEffect(() => {
    const stored = readStored();
    if (stored) {
      setLocationState(stored);
      setReady(true);
      return;
    }
    setReady(true);
    // Only prompt if browser supports geolocation and user hasn't been asked yet.
    try {
      const askedBefore = window.localStorage.getItem(LS_PROMPT_KEY);
      if (!askedBefore && typeof navigator !== "undefined" && "geolocation" in navigator) {
        setShouldPrompt(true);
      }
    } catch { /* ignore */ }
  }, []);

  const setLocation = useCallback((loc: Location) => {
    setLocationState(loc);
    writeStored(loc);
    setShouldPrompt(false);
  }, []);

  const dismissPrompt = useCallback(() => {
    setShouldPrompt(false);
    try { window.localStorage.setItem(LS_PROMPT_KEY, "1"); } catch { /* ignore */ }
  }, []);

  const requestGeolocation = useCallback(async () => {
    setShouldPrompt(false);
    try { window.localStorage.setItem(LS_PROMPT_KEY, "1"); } catch { /* ignore */ }
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    return new Promise<void>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const nearest = nearestKota(pos.coords.latitude, pos.coords.longitude);
          if (nearest) setLocation(nearest);
          resolve();
        },
        () => resolve(),
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 1000 * 60 * 60 * 24 },
      );
    });
  }, [setLocation]);

  const value = useMemo<Ctx>(
    () => ({ location, ready, shouldPrompt, setLocation, requestGeolocation, dismissPrompt }),
    [location, ready, shouldPrompt, setLocation, requestGeolocation, dismissPrompt],
  );

  return <LocationCtx.Provider value={value}>{children}</LocationCtx.Provider>;
}

/**
 * Returns the user's preferred location with a safe Jakarta fallback. Works
 * outside a provider too (returns Jakarta) so server-side renders never crash.
 */
export function useDefaultLocation(): Location {
  const ctx = useContext(LocationCtx);
  return ctx?.location ?? FALLBACK();
}

export function useLocationContext(): Ctx {
  const ctx = useContext(LocationCtx);
  if (ctx) return ctx;
  // Inert fallback so hooks compile without provider.
  return {
    location: FALLBACK(),
    ready: false,
    shouldPrompt: false,
    setLocation: () => {},
    requestGeolocation: async () => {},
    dismissPrompt: () => {},
  };
}